import { memo, useState } from 'react'
import _ from 'lodash'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FilterIcon, ChevronFilledIcon, SearchIcon } from '@/components/icons'

const SearchAndFilter = memo(
    ({
        searchPlaceholder = 'Search by file name',
        searchValue = '',
        onSearchChange = () => {},
        filterFormats = [],
        onClearFormatFilters = () => {},
        onApplyFilters = () => {},
        showClearButton = false,
        onClearSelection = () => {},
        disableClearButton = false,
        clearButtonText = 'Clear selection',
        availableFormats = ['square', 'vertical', 'horizontal'],
        className = '',
        showFormatFilter = true,
    }) => {
        const [showFilters, setShowFilters] = useState(false)
        const [tempFilterFormats, setTempFilterFormats] = useState(new Set())

        const handleOpenFilters = () => {
            setTempFilterFormats(new Set(filterFormats))
            setShowFilters(true)
        }

        const handleTempFormatToggle = (format) => {
            const newTempFormats = new Set(tempFilterFormats)

            if (newTempFormats.has(format)) newTempFormats.delete(format)
            else newTempFormats.add(format)

            setTempFilterFormats(newTempFormats)
        }

        const handleApply = () => {
            onApplyFilters(tempFilterFormats)
            setShowFilters(false)
        }

        const handleCancel = () => {
            setTempFilterFormats(new Set())
            onClearFormatFilters()
            setShowFilters(false)
        }

        return (
            <>
                <div className={cn('tw:flex tw:gap-4', className)}>
                    <div className="tw:relative tw:w-full">
                        <SearchIcon className="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:w-6 tw:h-6 tw:text-muted-foreground tw:pointer-events-none" />
                        <Input
                            className="tw:pl-10"
                            placeholder={searchPlaceholder}
                            value={searchValue}
                            onChange={(e) => onSearchChange(e.target.value)}
                        />
                    </div>

                    {showFormatFilter && (
                        <div className="tw:relative">
                            <button
                                onClick={handleOpenFilters}
                                className={cn(
                                    'tw:inline-flex tw:items-center tw:justify-center tw:gap-1 tw:whitespace-nowrap tw:!rounded-xs',
                                    'tw:!text-sm tw:font-medium tw:transition-all tw:disabled:pointer-events-none tw:disabled:opacity-50',
                                    "[&_svg]:tw:pointer-events-none [&_svg:not([class*='size-'])]:tw:size-4 tw:shrink-0 [&_svg]:tw:shrink-0",
                                    'tw:outline-none tw:focus-visible:border-ring tw:focus-visible:ring-ring/50 tw:focus-visible:ring-[3px]',
                                    'tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:aria-invalid:border-destructive',
                                    'tw:bg-secondary tw:border-1 tw:text-secondary-foreground tw:shadow-xs tw:hover:bg-secondary-hover tw:border-blue-500',
                                    'tw:h-9 tw:px-3 tw:py-2 tw:has-[>svg]:px-2 tw:!min-w-[76px] tw:max-w-[76px]',
                                )}
                            >
                                <FilterIcon className="tw:w-5 tw:h-5" />
                                <ChevronFilledIcon
                                    className={cn(
                                        'tw:transition-transform tw:w-6 tw:h-6 tw:min-h-6 tw:min-w-6',
                                        showFilters ? 'tw:rotate-180' : '',
                                    )}
                                />
                            </button>

                            {showFilters && (
                                <div className="tw:absolute tw:top-full tw:right-0 tw:mt-2 tw:bg-[#071727] tw:border tw:border-[#3B5168] tw:rounded-[2px] tw:p-2 tw:min-w-[240px] tw:z-[1000] tw:shadow-[0px_4px_24px_0px_#05111D40]">
                                    <p className="tw:pt-1 tw:text-sm tw:text-[#899CBD] tw:!mb-2">Format</p>
                                    {availableFormats.map((format) => (
                                        <div key={format} className="tw:mb-1">
                                            <label className="tw:!flex tw:gap-2 tw:items-center tw:py-1 tw:cursor-pointer tw:rounded tw:text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={tempFilterFormats.has(format)}
                                                    onChange={() => handleTempFormatToggle(format)}
                                                />
                                                {_.upperFirst(format)}
                                            </label>
                                        </div>
                                    ))}

                                    <div className="tw:flex tw:gap-2 tw:!mt-4">
                                        <Button variant="secondary" className="tw:h-7 tw:flex-1" onClick={handleCancel}>
                                            Cancel
                                        </Button>
                                        <Button className="tw:h-7 tw:flex-1" onClick={handleApply}>
                                            Apply
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {showClearButton && (
                        <Button variant="secondary" disabled={disableClearButton} onClick={onClearSelection}>
                            {clearButtonText}
                        </Button>
                    )}
                </div>

                {showFilters && (
                    <div
                        className="tw:fixed tw:top-0 tw:left-0 tw:right-0 tw:bottom-0 tw:z-[999]"
                        onClick={() => setShowFilters(false)}
                    />
                )}
            </>
        )
    },
)

SearchAndFilter.displayName = 'SearchAndFilter'

export default SearchAndFilter
